<template>
  <div
    class="character-counter"
    :class="{
      'character-counter--warning': isWarning,
      'character-counter--over': isOver,
    }"
    :aria-label="`${remaining} characters remaining`"
  >
    <svg
      v-if="showRing"
      viewBox="0 0 20 20"
      class="character-counter__ring"
      aria-hidden="true"
    >
      <circle
        cx="10"
        cy="10"
        r="9"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        opacity="0.2"
      />
      <circle
        cx="10"
        cy="10"
        r="9"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        :stroke-dasharray="circumference"
        :stroke-dashoffset="progressOffset"
        transform="rotate(-90 10 10)"
      />
    </svg>
    <span v-if="showNumber">{{ remaining }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

interface Props {
  current: number;
  max: number;
  warningThreshold?: number;
}

const props = withDefaults(defineProps<Props>(), {
  warningThreshold: 20,
});

const circumference = 2 * Math.PI * 9;

const remaining = computed(() => props.max - props.current);

const isWarning = computed(
  () => remaining.value <= props.warningThreshold && remaining.value > 0
);

const isOver = computed(() => remaining.value < 0);

const showRing = computed(() => remaining.value <= props.warningThreshold);

const showNumber = computed(() => remaining.value <= props.warningThreshold);

const progressOffset = computed(() => {
  const progress = Math.max(0, remaining.value) / props.max;
  return circumference * (1 - progress);
});
</script>

<style scoped lang="scss">
.character-counter {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.5);
  position: relative;

  &--warning {
    color: #f59e0b;
  }

  &--over {
    color: #ef4444;
  }

  &__ring {
    width: 24px;
    height: 24px;
    position: absolute;
  }

  span {
    position: relative;
    z-index: 1;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }
}
</style>
